import { useEffect } from "react";

import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { t } from "translations";

import { useGetAccount } from "apps-common/hooks/useGetAccount";
import { BillingPeriod, PaymentMethodState } from "apps-common/types";
import { track } from "apps-common/utils/analytics";
import { throwError } from "apps-common/utils/errorHandler";
import { Header } from "ui";
import { Button } from "ui/components/Button";
import { Loader } from "ui/components/Loader";
import { MainContainer } from "ui/styles/containers";
import { Paragraph } from "ui/styles/text";
import { media } from "ui/themes/signup";

import {
  NavigationRow,
  PreviewRow,
  SectionCard,
} from "../components/SectionCard";
import { useRenewSubscription } from "../hooks/useRenewSubscription";
import { routes } from "../routes";
import { getPaymentMethodStatus, isRenewAllowed } from "../utils/member";
import { paymentMethodDetail } from "../utils/paymentMethod";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 120px;
  align-items: center;

  ${media.large`
     margin-top: 60px;
     align-items: flex-start;

     > p {
     margin-left: 0;
     }
  `}
`;

export const ReviewPaymentMethodPage = () => {
  const navigate = useNavigate();
  const intl = useIntl();

  const {
    data: accountData,
    isFetching: isFetchingAccount,
    isError: isGetAccountError,
    error: getAccountError,
  } = useGetAccount();

  const mutation = useRenewSubscription();
  const {
    mutate,
    error: renewError,
    isError: isRenewError,
    isSuccess: isRenewSuccess,
    isPending: isRenewPending,
  } = mutation;

  // In case of mutation is success we invalidate account data so it's refreshed on frontpage
  useEffect(() => {
    if (isRenewSuccess) {
      navigate(routes.updatePaymentMethodSuccessRenew);
    }
  }, [isRenewSuccess, navigate]);

  if (isFetchingAccount || isRenewPending) {
    return (
      <>
        <Header
          appType="hub"
          pageType="create"
          title={t("membership_signup_payment_method_title")}
          onBackClick={() => navigate(routes.index)}
        />
        <MainContainer $padding="10px 0" $maxWidth="794px">
          <Loader />
        </MainContainer>
      </>
    );
  }

  if (isGetAccountError || !accountData) {
    throw throwError("errorOnGetAccount", getAccountError);
  }

  if (isRenewError) {
    throw throwError("errorOnRenewSubscription", renewError);
  }

  const paymentMethod =
    accountData.account.paymentMethods.find((pm) => pm.isDefault) ??
    accountData.account.paymentMethods[0];

  const { billToContact } = accountData.account;

  if (!billToContact) {
    // If user doesn't have an address on file, then there is nothing to review here
    throw throwError("hubGeneralError", "No billing address found for user.");
  }

  const { address1, city, country, postalCode, state } = billToContact;
  const STRING_ADDRESS = `${address1}, ${city}, ${
    state ?? ""
  } ${postalCode}, ${country}`;

  const paymentDetails = paymentMethodDetail(paymentMethod);

  const currentSubscription = accountData.account.currentSubscription;
  if (!currentSubscription) {
    // Should never happen, as at this point user should have a subscription
    throw throwError("hubGeneralError", "No subscription found for user.");
  }

  const isRenewFlow = isRenewAllowed(currentSubscription.renewalState);
  const billingPeriod =
    currentSubscription.recurringFee?.billingPeriod ?? BillingPeriod.Months;

  // no payment user doesnt have access to review payment page
  const isDirectRenewPossible =
    accountData.account.isPaymentMethodReusableInRenew &&
    getPaymentMethodStatus(paymentMethod) !== PaymentMethodState.EXPIRED;

  const renewSubscription = () => {
    track({
      event: "CTA Clicked",
      payload: {
        cta: "submit",
        action: "renew_membership",
      },
    });
    mutate(billingPeriod);
  };
  const renewWithAddressAndPaymentMethod = () => {
    track({
      event: "CTA Clicked",
      payload: {
        cta: "next",
        action: "renew_next_step",
        step: "review_payment",
      },
    });
    navigate(routes.updateAddressInfo);
  };
  const paymentCTAKey = isRenewFlow
    ? "membership_hub_renew_with_payment"
    : "membership_hub_edit_payment_method";

  return (
    <>
      <Header
        appType="hub"
        pageType="create"
        title={t("membership_signup_payment_method_title")}
        onBackClick={() => navigate(-1)}
      />
      <MainContainer $padding="10px 0" $maxWidth="794px">
        <SectionCard>
          <PreviewRow
            label={t("membership_hub_payment_details")}
            rowValues={paymentDetails}
          />
          <PreviewRow
            label={t("membership_hub_billing_address")}
            rowValues={[{ value: STRING_ADDRESS, color: "grayLight" }]}
          />
          <NavigationRow
            label={intl.formatMessage({
              id: paymentCTAKey,
            })}
            href={routes.updateAddressInfo}
            onClick={() => {
              isRenewFlow
                ? track({
                    event: "CTA Clicked",
                    payload: {
                      cta: "next",
                      action: "renew_next_step",
                      step: "review_payment",
                    },
                  })
                : track({
                    event: "Link Clicked",
                    payload: {
                      cta: "edit_payment",
                      location: "body",
                    },
                  });
            }}
          />
        </SectionCard>
        {isRenewFlow && (
          <Container>
            <Button
              onClick={
                isDirectRenewPossible
                  ? renewSubscription
                  : renewWithAddressAndPaymentMethod
              }
            >
              {t("membership_hub_renew_submit")}
            </Button>
            <Paragraph
              $fontSize="xsmall"
              $fontWeight="400"
              $lineHeight="16px"
              $color="grayLight"
              $textAlign="justify"
              $margin={"20px 24px 0 24px"}
            >
              {t("membership_signup_payment_disclaimer")}
            </Paragraph>
          </Container>
        )}
      </MainContainer>
    </>
  );
};
